import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { hkDateISO } from '../utils/dateUtils';
import { isValidEmail, findInvalidEmails } from '../utils/emailUtils';
import { fieldConfigService } from '../services/fieldConfigService';
import { copyTextToClipboard } from '../utils/clipboard.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiFetch(path, options = {}) {
  const response = await fetch(`${API_BASE}/${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  // A 503 from the maintenance guard is not an application error: send the
  // user to the maintenance screen instead of surfacing a red toast.
  if (response.status === 503) {
    if (window.location.pathname !== '/maintenance') {
      window.location.assign('/maintenance');
    }
    // Never resolve: the page is navigating away, so callers must not render
    // an error state in the meantime.
    await new Promise(() => {});
  }

  if (!response.ok) {
    let message = 'Request failed';
    try {
      const err = await response.json();
      message = err.error || err.message || message;
    } catch {
      message = response.statusText || message;
    }
    throw new Error(message);
  }


  const ct = response.headers.get('content-type') || '';
  if (ct.includes('application/json')) return response.json();
  return response.text();
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isOnboardingForm(formId) {
  return formId === 'onboarding-registration' || (formId && formId.startsWith('onboarding-registration-copy'));
}

const styles = {
  page: {
    maxWidth: 'none',
    margin: 0,
    padding: '0 0 24px',
  },
  pageTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 18,
    flexWrap: 'wrap',
  },
  pageTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.2,
    fontWeight: 700,
    color: 'var(--company-primary-color, #273947)',
  },
  clinicBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 38,
    height: 26,
    padding: '0 10px',
    borderRadius: 999,
    background: '#e3f2fd',
    color: 'var(--company-primary-color, #273947)',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  sectionContainer: {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    padding: 20,
    border: '1px solid #e9ecef',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: '1px solid #e9ecef',
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
    margin: 0,
  },
  searchInput: {
    width: 250,
    maxWidth: '100%',
    padding: '10px 14px',
    border: '1px solid #e9ecef',
    borderRadius: 6,
    fontSize: 14,
    color: '#333',
    background: '#fff',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 20,
    marginTop: 15,
  },
  card: {
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 220,
    transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 15,
  },
  cardName: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
    margin: 0,
    lineHeight: 1.35,
  },
  cardMeta: {
    fontSize: 12,
    color: '#6c757d',
    marginBottom: 20,
    flexGrow: 1,
    lineHeight: 1.6,
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  },
  publishedBadge: {
    background: '#e8f5e9',
    color: '#388e3c',
    borderColor: '#c8e6c9',
  },
  draftBadge: {
    background: '#f8f9fa',
    color: '#6c757d',
    borderColor: '#dee2e6',
  },
  cardActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'nowrap',
    borderTop: '1px solid #e9ecef',
    paddingTop: 15,
    marginTop: 'auto',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: '#333',
    background: 'transparent',
    border: '1px solid #e9ecef',
    borderRadius: 4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  },
  // Applied on top of a button style when a feature is switched off, so the
  // control stays visible but reads clearly as unavailable.
  btnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    color: '#9aa0a6',
    background: '#f5f6f7',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6c757d',
    fontSize: 15,
  },
  loadingShell: {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid #e9ecef',
    padding: 20,
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6c757d',
    fontSize: 15,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 8,
    padding: 28,
    width: '100%',
    maxWidth: 500,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
    marginBottom: 4,
    marginTop: 0,
  },
  modalSubtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 20,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e9ecef',
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e9ecef',
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
  },
  btnFillForm: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--company-primary-color, #273947)',
    background: '#f2f8fa',
    border: '1px dashed #9fc4d0',
    borderRadius: 6,
    cursor: 'pointer',
  },
  prefillDone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '9px 12px',
    background: '#f2f8fa',
    border: '1px solid #cfe3ea',
    borderRadius: 6,
  },
  prefillDoneText: {
    fontSize: 13,
    color: 'var(--company-primary-color, #273947)',
    fontWeight: 500,
  },
  prefillLinkBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--company-primary-color, #273947)',
    fontSize: 12,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0 4px',
  },
  labelHint: {
    fontWeight: 400,
    color: '#6c757d',
    fontSize: 12,
  },
  prefillHint: {
    fontSize: 12,
    color: '#6c757d',
    marginBottom: 8,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: '#333',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
  },
  btnCancel: {
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    color: '#333',
    background: 'transparent',
    border: '1px solid #e9ecef',
    borderRadius: 4,
    cursor: 'pointer',
  },
  btnGenerate: {
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
  },
  linkResult: {
    marginTop: 16,
    padding: 16,
    background: '#f8f9fa',
    borderRadius: 8,
    border: '1px solid #e9ecef',
  },
  linkUrlInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e9ecef',
    borderRadius: 6,
    fontSize: 13,
    background: '#fff',
    boxSizing: 'border-box',
    marginBottom: 8,
  },
  linkExpiry: {
    fontSize: 12,
    color: '#6b7280',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '12px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    zIndex: 2000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  toastSuccess: { background: 'var(--company-primary-color, #273947)' },
  toastError: { background: '#dc3545' },
  previewModalContent: {
    background: '#fff',
    borderRadius: 8,
    padding: 0,
    width: '90%',
    maxWidth: 900,
    height: '85vh',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid #e9ecef',
    background: '#f8f9fa',
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
    margin: 0,
  },
  previewCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px 8px',
    lineHeight: 1,
  },
  previewFrame: {
    flex: 1,
    border: 'none',
    width: '100%',
  },
};

export default function ClinicTemplatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showConfirm } = useModal();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [linkDoctorEmail, setLinkDoctorEmail] = useState('');
  const [linkCcEmail, setLinkCcEmail] = useState('');
  const [linkBccEmail, setLinkBccEmail] = useState('');
  const [linkExpiryDate, setLinkExpiryDate] = useState('');
  const [linkNeverExpired, setLinkNeverExpired] = useState(false);
  const [linkType, setLinkType] = useState('reusable');
  const [linkGenerating, setLinkGenerating] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  // Pre-fill: fields available on the selected template, and the values staff typed.
  const [prefillValues, setPrefillValues] = useState({});

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    loadForms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Coming back from "Fill Form First": restore the half-completed Generate Link
  // form and attach the answers the user just saved, so the flow continues where
  // it left off instead of starting over.
  useEffect(() => {
    const raw = sessionStorage.getItem('prefillReturn');
    if (!raw) return;
    sessionStorage.removeItem('prefillReturn');

    try {
      const ctx = JSON.parse(raw);
      const saved = sessionStorage.getItem('prefillValues');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Answers are stored against the form they were typed into. Ignore (and
        // clear) anything belonging to a different template, otherwise switching
        // templates would carry the previous form's answers across.
        if (parsed?.formId === ctx.formId) {
          setPrefillValues(parsed.values || {});
        } else {
          sessionStorage.removeItem('prefillValues');
        }
      }

      setLinkModal({ formId: ctx.formId, formName: ctx.formName });
      setLinkDoctorEmail(ctx.doctorEmail || '');
      setLinkCcEmail(ctx.ccEmail || '');
      setLinkBccEmail(ctx.bccEmail || '');
      setLinkExpiryDate(ctx.expiryDate || '');
      setLinkNeverExpired(!!ctx.neverExpired);
      setLinkType(ctx.linkType || 'reusable');
      setLinkResult(null);
      setLinkGenerating(false);
    } catch {
      // A malformed context just means no auto-reopen; nothing is lost.
      sessionStorage.removeItem('prefillValues');
    }
  }, []);

  const loadForms = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('admin/forms');
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredForms = forms.filter((form) => {
    if (!search.trim()) return true;
    return (form.name || '').toLowerCase().includes(search.toLowerCase());
  });

  const openGenerateLinkModal = (form) => {
    setLinkModal({ formId: form.id, formName: form.name });
    setLinkDoctorEmail('');
    setLinkCcEmail('');
    setLinkBccEmail('');
    setLinkExpiryDate('');
    setLinkNeverExpired(false);
    setLinkType('reusable');
    setLinkResult(null);
    setLinkGenerating(false);
    setPrefillValues({});
  };

  // Number of pre-filled answers currently attached to this link.
  const prefillCount = Object.values(prefillValues)
    .filter((v) => String(v ?? '').trim() !== '').length;

  // Opens the real form so staff can fill it in. The form is told to come back
  // here on save (prefillReturn) instead of creating a draft, so the answers end
  // up on the link we are about to generate rather than in My Submissions.
  const handleFillFormFirst = (mode = 'new') => {
    if (!linkModal) return;
    // "new" means start from a blank form; "edit" keeps what was entered before.
    if (mode === 'new') sessionStorage.removeItem('prefillValues');
    sessionStorage.setItem('prefillReturn', JSON.stringify({
      formId: linkModal.formId,
      formName: linkModal.formName,
      returnPath: window.location.pathname,
      doctorEmail: linkDoctorEmail,
      ccEmail: linkCcEmail,
      bccEmail: linkBccEmail,
      expiryDate: linkExpiryDate,
      neverExpired: linkNeverExpired,
      linkType,
    }));
    navigate(`/form/${encodeURIComponent(linkModal.formId)}?internal=1&prefill=1`);
  };

  const clearPrefill = () => {
    setPrefillValues({});
    sessionStorage.removeItem('prefillValues');
  };

  const closeGenerateLinkModal = () => {
    setLinkModal(null);
    setLinkResult(null);
    setPrefillValues({});
    // Abandoning the dialog discards the pre-fill too, so reopening it later
    // does not resurrect answers the user chose not to use.
    sessionStorage.removeItem('prefillValues');
    sessionStorage.removeItem('prefillReturn');
  };

  const handleGenerateLink = async () => {
    if (!linkModal) return;
    if (!linkDoctorEmail.trim()) {
      showToast('Form Recipient Email is required', 'error');
      return;
    }
    if (!isValidEmail(linkDoctorEmail)) {
      showToast('Form Recipient Email is not a valid email address', 'error');
      return;
    }
    // CC is optional but accepts a comma-separated list, so every entry is checked.
    const invalidCc = findInvalidEmails(linkCcEmail);
    if (invalidCc.length > 0) {
      showToast(`Invalid CC email: ${invalidCc.join(', ')}`, 'error');
      return;
    }

    const invalidBcc = findInvalidEmails(linkBccEmail);
    if (invalidBcc.length > 0) {
      showToast(`Invalid BCC email: ${invalidBcc.join(', ')}`, 'error');
      return;
    }
    if (!linkNeverExpired && !linkExpiryDate) {
      showToast('Link Expiry Date is required', 'error');
      return;
    }
    // `min` only constrains the picker; a typed or pasted date still gets through,
    // and a past date would otherwise be clamped to the 1-hour floor below and
    // produce a link that expires almost immediately.
    if (!linkNeverExpired && linkExpiryDate < hkDateISO()) {
      showToast('Link Expiry Date cannot be in the past', 'error');
      return;
    }
    setLinkGenerating(true);
    try {
      // Calculate expiration hours from date
      let expirationHours = null;
      if (!linkNeverExpired && linkExpiryDate) {
        const expiryMs = new Date(linkExpiryDate).getTime() - Date.now();
        expirationHours = Math.max(1, Math.ceil(expiryMs / 3600000));
      }
      const payload = {
        formId: linkModal.formId,
        doctorEmail: linkDoctorEmail.trim(),
        ccEmail: linkCcEmail.trim() || null,
        bccEmail: linkBccEmail.trim() || null,
        expirationHours,
        reusable: linkType === 'reusable',
        // Only send fields staff actually filled in; blanks are dropped so they
        // never overwrite a field with an empty value.
        prefillFieldValues: Object.fromEntries(
          Object.entries(prefillValues).filter(([, v]) => String(v || '').trim() !== '')
        ),
      };
      const result = await apiFetch('formlinks', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setLinkResult({ url: result.url || '', expiresAt: result.expiresAt });
      // The answers now live on the generated link; keeping the local copy would
      // silently pre-fill the next link the user creates.
      setPrefillValues({});
      sessionStorage.removeItem('prefillValues');
      showToast('Link generated successfully');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLinkGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    const copied = await copyTextToClipboard(text);
    if (copied) showToast('Copied to clipboard');
    else showToast('Could not copy — please copy manually', 'error');
  };

  const getFormUrl = (form, { skipConsent = false } = {}) => {
    if (isOnboardingForm(form.id)) {
      return skipConsent ? '/onboarding?skipConsent=1&internal=1' : '/onboarding?internal=1';
    }
    return `/form/${encodeURIComponent(form.id)}?internal=1`;
  };

  const handleOpenForm = async (form) => {
    // Always skipped: this build has no consent step after the form is opened, so
    // offering the choice would lead to a route that does not exist.
    const skipConsent = true;
    sessionStorage.setItem('formReturnPath', window.location.pathname);
    // Same tab: a new tab per click allowed the same form to be open several
    // times at once, which led to duplicate submissions.
    navigate(getFormUrl(form, { skipConsent }));
  };

  const renderGrid = () => {
    if (filteredForms.length === 0) {
      return (
        <div style={styles.emptyState}>
          <p>{search ? 'No templates match your search.' : 'No assigned templates found.'}</p>
        </div>
      );
    }

    return (
      <div style={styles.grid}>
        {filteredForms.map((form) => {
          return (
            <div
              key={form.id}
              style={styles.card}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#4a90e2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
            >
              <div style={styles.cardHeader}>
                <h3 style={styles.cardName}>{form.name || 'Untitled Form'}</h3>
              </div>

              <div style={styles.cardMeta}>
                <div>Assigned: {formatDate(form.createdAt || form.updatedAt || form.modifiedAt)}</div>
                {form.description ? <div style={{ marginTop: 8 }}>{form.description}</div> : null}
              </div>

              <div style={styles.cardActions}>
                <button style={styles.btnPrimary} onClick={() => handleOpenForm(form)}>
                  Open Form
                </button>
                {/* Greyed out and unclickable while link generation is switched off
                    for clinic staff (Features:ClinicGenerateLinkEnabled). Kept visible
                    so staff can see the feature exists but is temporarily unavailable.
                    The API blocks it as well, so this is presentation only.
                    Defaults to enabled when the flag is unset. */}
                <button
                  style={{
                    ...styles.btnSecondary,
                    ...(user?.canGenerateLinks === false ? styles.btnDisabled : {}),
                  }}
                  onClick={() => openGenerateLinkModal(form)}
                  disabled={user?.canGenerateLinks === false}
                  title={user?.canGenerateLinks === false
                    ? 'Link generation is temporarily unavailable'
                    : undefined}
                >
                  Generate Link
                </button>
                <button style={styles.btnSecondary} onClick={() => setPreviewForm(form)}>
                  Preview
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.pageTitleRow}>
          <h1 style={styles.pageTitle}>My Templates</h1>
          <span style={styles.clinicBadge}>{(user?.clinicCode || user?.clinicName || 'CL').toString()}</span>
        </div>
        <div style={styles.loadingShell}>
          <div style={styles.loadingState}>
            <p>Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageTitleRow}>
        <h1 style={styles.pageTitle}>My Templates</h1>
        <span style={styles.clinicBadge}>{(user?.clinicCode || user?.clinicName || 'CL').toString()}</span>
      </div>

      <div style={styles.sectionContainer}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Assigned Templates</h2>
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
            onFocus={(e) => {
              e.target.style.borderColor = '#4a90e2';
              e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.12)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e9ecef';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {renderGrid()}
      </div>

      {linkModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <h3 style={styles.modalTitle}>Generate Form Link</h3>
              <button
                type="button"
                onClick={closeGenerateLinkModal}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280', padding: '0 4px', lineHeight: 1 }}
              >
                &times;
              </button>
            </div>
            <p style={styles.modalSubtitle}>Template: {linkModal.formName}</p>

            {!linkResult ? (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Form Recipient Email <span style={{ color: '#dc3545' }}>*</span></label>
                  <input
                    type="email"
                    style={styles.input}
                    placeholder="e.g. doctor@example.com"
                    value={linkDoctorEmail}
                    onChange={(e) => setLinkDoctorEmail(e.target.value)}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Submission of this form will be sent to this email.
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>CC Email</label>
                  <input
                    type="email"
                    multiple
                    style={styles.input}
                    placeholder="e.g. recep@example.com"
                    value={linkCcEmail}
                    onChange={(e) => setLinkCcEmail(e.target.value)}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Copied on the submission email and visible to every recipient. Separate multiple addresses with a comma.
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>BCC Email</label>
                  <input
                    type="email"
                    multiple
                    style={styles.input}
                    placeholder="e.g. records@example.com"
                    value={linkBccEmail}
                    onChange={(e) => setLinkBccEmail(e.target.value)}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Copied on the submission email but hidden from the other recipients. Separate multiple addresses with a comma.
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Link Expiry Date <span style={{ color: '#dc3545' }}>*</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="date"
                      style={{ ...styles.input, flex: 1, opacity: linkNeverExpired ? 0.5 : 1 }}
                      value={linkExpiryDate}
                      min={hkDateISO()}
                      onChange={(e) => setLinkExpiryDate(e.target.value)}
                      disabled={linkNeverExpired}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={linkNeverExpired}
                        onChange={(e) => {
                          setLinkNeverExpired(e.target.checked);
                          if (e.target.checked) setLinkExpiryDate('');
                        }}
                      />
                      Never Expired
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Link Type <span style={{ color: '#dc3545' }}>*</span></label>
                  <select style={styles.select} value={linkType} onChange={(e) => setLinkType(e.target.value)}>
                    <option value="reusable">Reusable Link (Allow submissions by multiple patient)</option>
                    <option value="single">One-Off Link (Single patient only)</option>
                  </select>
                </div>

                {/* Optional pre-fill. Staff open the real form, fill in whatever the
                    patient should not have to retype, and save; those answers are
                    attached to the link so the patient sees them already filled in. */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Pre-fill <span style={styles.labelHint}>(optional)</span>
                  </label>
                  {prefillCount > 0 ? (
                    <div style={styles.prefillDone}>
                      <span style={styles.prefillDoneText}>
                        {prefillCount} field{prefillCount === 1 ? '' : 's'} pre-filled
                      </span>
                      <span>
                        <button type="button" style={styles.prefillLinkBtn} onClick={() => handleFillFormFirst('edit')}>
                          Edit
                        </button>
                        <button type="button" style={styles.prefillLinkBtn} onClick={clearPrefill}>
                          Clear
                        </button>
                      </span>
                    </div>
                  ) : (
                    <>
                      <button type="button" style={styles.btnFillForm} onClick={() => handleFillFormFirst('new')}>
                        Pre-fill Form
                      </button>
                      <div style={styles.prefillHint}>
                        Pre-fill fields on the form if needed. Your entries will be saved
                        and shown to the patient when they open the link.
                      </div>
                    </>
                  )}
                </div>

                <div style={styles.modalActions}>
                  <button style={styles.btnCancel} onClick={closeGenerateLinkModal}>Cancel</button>
                  <button
                    style={{ ...styles.btnGenerate, opacity: linkGenerating ? 0.7 : 1 }}
                    onClick={handleGenerateLink}
                    disabled={linkGenerating}
                  >
                    {linkGenerating ? 'Generating...' : 'Generate Link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.linkResult}>
                  <label style={styles.label}>Generated Link:</label>
                  <input
                    type="text"
                    style={styles.linkUrlInput}
                    value={linkResult.url}
                    readOnly
                    onClick={(e) => e.target.select()}
                  />
                  <div style={styles.linkExpiry}>
                    {linkResult.expiresAt ? `Expires: ${formatDate(linkResult.expiresAt)}` : 'This link never expires.'}
                  </div>
                </div>

                <div style={styles.modalActions}>
                  <button style={styles.btnSecondary} onClick={() => copyToClipboard(linkResult.url)}>
                    Copy Link
                  </button>
                  <button style={styles.btnCancel} onClick={closeGenerateLinkModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {previewForm && (
        <div style={styles.modalOverlay} onClick={() => setPreviewForm(null)}>
          <div style={styles.previewModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.previewHeader}>
              <h3 style={styles.previewTitle}>{previewForm.name || 'Preview'}</h3>
              <button style={styles.previewCloseBtn} onClick={() => setPreviewForm(null)}>✕</button>
            </div>
            <iframe
              src={`${API_BASE}/admin/forms/${encodeURIComponent(previewForm.id)}/preview`}
              style={styles.previewFrame}
              title="PDF Preview"
            />
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...styles.toast, ...(toast.type === 'error' ? styles.toastError : styles.toastSuccess) }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
